import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type Page } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { DataTable, PageHead, Pager, useKeysetPage } from '../ui/table';
import { ItemId, Num, Time } from '../ui/values';
import { FilterSearch, useUrlFilter } from './common';
import { paths } from './paths';
import type { AdminItemRow } from './types';

/** Item-Katalog (design.md §6): Item-ID · Varianten · in Kisten · Gesamtmenge · zuerst gesehen. */
export function ItemsPage() {
  const page = useKeysetPage();
  const [q, setQ] = useUrlFilter('q');
  const query = { q, cursor: page.cursor, limit: page.limit };
  const list = useQuery({
    queryKey: ['admin', 'items', query],
    queryFn: () => api.get<Page<AdminItemRow>>('/admin/items', query),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="stack">
      <PageHead title="Items" context="Alle Items, die in irgendeiner Kiste gemeldet wurden" />
      <div className="filters">
        <FilterSearch label="Item-ID oder Name" value={q} onChange={setQ} />
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <DataTable
        label="Items"
        rows={list.data?.items}
        loading={list.isPending}
        rowKey={(i) => i.id}
        rowHref={(i) => paths.item(i.id)}
        empty={q ? 'Keine Items für diese Suche.' : 'Noch keine Items gemeldet.'}
        columns={[
          { key: 'id', header: 'Item-ID', cell: (i) => <ItemId id={i.id} /> },
          { key: 'name', header: 'Name', hide: 'sm', cell: (i) => i.name ?? <span className="muted">–</span> },
          { key: 'variants', header: 'Varianten', num: true, hide: 'md', cell: (i) => <Num value={i.variantCount} /> },
          { key: 'chests', header: 'in Kisten', num: true, cell: (i) => <Num value={i.chestCount} /> },
          { key: 'total', header: 'Gesamtmenge', num: true, cell: (i) => <Num value={i.total} /> },
          { key: 'seen', header: 'zuerst gesehen', hide: 'sm', cell: (i) => <Time iso={i.firstSeenAt} /> },
        ]}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />
    </div>
  );
}
